import path from 'path'
import fs from 'fs-extra'

import { buildMockIndex, parseMockModule } from './mockFileUtils'

export interface MockBundleData {
  enable: boolean
  data: unknown
  delay: number
  status: number
  isBinary?: boolean
}

export interface MockBundle {
  [key: string]: MockBundleData
}

export interface BundleOptions {
  mockDir: string
  includeDisabled?: boolean
  log?: boolean
}

const DEFAULT_BUNDLE_OPTIONS: Partial<BundleOptions> = {
  includeDisabled: true,
  log: true
}

async function bundleMockFiles(options: BundleOptions): Promise<MockBundle> {
  const opts = { ...DEFAULT_BUNDLE_OPTIONS, ...options }
  const { mockDir, log } = opts

  if (!fs.existsSync(mockDir)) {
    if (log) {
      console.log(`[mock-bundler] Mock directory does not exist: ${mockDir}`)
    }
    return {}
  }

  const mockFileMap = await buildMockIndex(mockDir)
  const bundle: MockBundle = {}
  let bundledCount = 0
  let skippedCount = 0

  for (const [key, filePath] of mockFileMap) {
    try {
      const mockInfo = await parseMockModule(filePath)

      if (!mockInfo.serializable) {
        skippedCount++
        if (log) {
          console.log(`[mock-bundler] Skipping non-serializable mock: ${key}`)
        }
        continue
      }

      bundle[key] = {
        enable: mockInfo.config.enable,
        data: mockInfo.config.data,
        delay: mockInfo.config.delay,
        status: mockInfo.config.status,
        isBinary: mockInfo.isBinary
      }

      bundledCount++
    } catch (error) {
      if (log) {
        console.error(`[mock-bundler] Failed to bundle mock ${key}:`, error)
      }
    }
  }

  if (log) {
    console.log(`[mock-bundler] Bundled ${bundledCount} mocks, skipped ${skippedCount} non-serializable mocks`)
  }

  return bundle
}

function writeMockBundle(bundle: MockBundle, outputPath: string): void {
  const outputDir = path.dirname(outputPath)

  if (!fs.existsSync(outputDir)) {
    fs.ensureDirSync(outputDir)
  }

  fs.writeFileSync(
    outputPath,
    JSON.stringify(bundle, null, 2),
    'utf-8'
  )
}

export { bundleMockFiles, writeMockBundle }
